import { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import { useHistory as useRouterHistory } from 'react-router-dom';
import { routes } from '../routes/routes';
import Filters from '../components/filter/Filters';
import Grid from '../components/grid/Grid';
import GridCell from '../components/grid/GridCell';
import { StorageKeys } from '../components/constants/storageKeys';
import Throbber from '../components/throbber/Throbber';
import Error from '../components/error/Error';
import useHistory from '../hooks/useHistory';
import useApi from '../api';
import queryConfig from '../util/queryConfig';
import { QueryKey } from '../constants/queryKey';
import usePersistedFilter from '../hooks/usePersistedFilter';

const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '';
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
};

const cleanHeader = (key) => {
    return key.replace(/_/g, ' ');
};

const HistoryView = () => {
    const routerHistory = useRouterHistory();
    const { getScenarios } = useApi();
    const [filter, setFilter] = usePersistedFilter('history');
    const [cursor, setCursor] = useState(null);
    const [page, setPage] = useState(1);

    const { data: scenariosData } = useQuery([QueryKey.Scenarios], async () => {
        const response = await getScenarios();
        return response.data;
    }, queryConfig);

    useEffect(() => {
        if (scenariosData?.scenarios?.length) {
            const valid = scenariosData.scenarios.some(s => s.scenario_id === filter.scenario);
            if (!filter.scenario || !valid) {
                setFilter(f => ({ ...f, scenario: scenariosData.scenarios[0].scenario_id }));
            }
        }
    }, [scenariosData]);

    const { history, isLoading, isError } = useHistory(filter, cursor);

    const data = useMemo(() => {
        if (!history?.rows) return [];
        return history.rows.map((row) => ({
            ...row,
            duration_fmt: formatDuration(row.video_duration),
        }));
    }, [history]);

    const columns = useMemo(() => {
        const fixed = [
            {
                Header: 'Status',
                accessor: 'status',
                Cell: (props) => <GridCell {...props} format="status" />,
                width: 100,
                sortType: 'alphanumeric',
            },
            {
                Header: 'Scenario',
                accessor: 'scenario_id',
                width: 100,
                sortType: 'alphanumeric',
            },
            {
                Header: 'Recipient',
                accessor: 'video_recipient',
                width: 160,
                sortType: 'alphanumeric',
            },
            {
                Header: 'Created',
                accessor: 'created_at',
                Cell: (props) => <GridCell {...props} format="date" />,
                width: 200,
                sortType: 'alphanumeric',
            },
            {
                Header: 'Duration',
                accessor: 'duration_fmt',
                width: 80,
                sortType: 'alphanumeric',
            },
            {
                Header: 'Views',
                accessor: 'watch_count',
                width: 70,
                sortType: 'numeric',
            },
        ];

        const dynamic = (history?.dataFields || []).map((field) => ({
            Header: cleanHeader(field),
            accessor: (row) => {
                const val = row.data?.[field];
                if (val == null) return '';
                if (typeof val === 'object') return JSON.stringify(val);
                return val;
            },
            id: `data_${field}`,
            width: 160,
            sortType: 'alphanumeric',
        }));

        return [...fixed, ...dynamic];
    }, [history]);

    const onFilterChange = (e, prop) => {
        setCursor(null);
        setPage(1);
        setFilter({
            ...filter,
            [prop]: e.target.value,
        });
    };

    const goNext = () => {
        if (!history?.pagination?.nextCursor) return;
        setCursor({ ...history.pagination.nextCursor, direction: 'next' });
        setPage(p => p + 1);
    };

    const goPrev = () => {
        if (!history?.pagination?.prevCursor) return;
        setCursor({ ...history.pagination.prevCursor, direction: 'prev' });
        setPage(p => Math.max(1, p - 1));
    };

    const goFirst = () => {
        setCursor(null);
        setPage(1);
    };

    const pagination = history?.pagination;

    return (
        <div className="view history-view">
            <Filters onFilterChange={onFilterChange} filter={filter} scenarios={scenariosData?.scenarios} />
            <h1 className="heading">History</h1>

            {isLoading ? (
                <Throbber title="Loading history" />
            ) : isError ? (
                <Error msg="Something went wrong loading history. Please try again later." />
            ) : data.length === 0 ? (
                <div>No video requests found for this period.</div>
            ) : (
                <>
                    <div className="history-grid">
                        <Grid
                            data={data}
                            columns={columns}
                            storageKey={StorageKeys.GridHistory}
                            getRowProps={() => ({
                                className: 'row',
                                style: { cursor: 'pointer' },
                            })}
                            onRowClick={(row) => {
                                if (row.original?.request_id) {
                                    routerHistory.push(routes.video(row.original.request_id));
                                }
                            }}
                        />
                    </div>
                    {pagination && pagination.totalPages > 1 && (
                        <div className="history-pagination">
                            <button
                                className="history-pagination__btn"
                                onClick={goFirst}
                                disabled={page === 1}
                            >
                                &laquo;
                            </button>
                            <button
                                className="history-pagination__btn"
                                onClick={goPrev}
                                disabled={!pagination.hasPrev}
                            >
                                &lsaquo;
                            </button>
                            <span className="history-pagination__info">
                                Page {page} of {pagination.totalPages}
                                <span className="history-pagination__count">({pagination.totalCount} results)</span>
                            </span>
                            <button
                                className="history-pagination__btn"
                                onClick={goNext}
                                disabled={!pagination.hasNext}
                            >
                                &rsaquo;
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default HistoryView;
